import { useCallback, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store';
import { useAuthHttp } from '@hooks';
import { LIST_SUPPLY_CHAIN_PROCESSES_URL } from '@constants';
import {
  addInboundShipments,
  addOutboundShipments,
} from '@features/supplyChain';

export interface ShipmentFilters {
  searchFilter: string;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const useShipments = () => {
  const authHttp = useAuthHttp();
  const dispatch = useAppDispatch();

  const fetchShipments = async (
    direction: 'Inbound' | 'Outbound',
    filters: ShipmentFilters
  ) => {
    const { data } = await authHttp.post(LIST_SUPPLY_CHAIN_PROCESSES_URL, {
      searchQuery: filters.searchFilter,
      type: direction,
      hasShipmentNote: direction === 'Inbound' ? true : null,
      ...(direction === 'Inbound'
        ? {
            grnFinalisationFromDate: filters.dateFrom || null,
            grnFinalisationToDate: filters.dateTo || null,
          }
        : {
            pickingNoteConfirmationFromDate: filters.dateFrom || null,
            pickingNoteConfirmationToDate: filters.dateTo || null,
            excludeStatuses: ['Draft', 'Ignored'],
          }),
      pagination: {
        lastItemSort: null,
        itemsPerPage: 50,
      },
    });

    return data;
  };

  const {
    supplyChain: { inboundShipments, outboundShipments },
  } = useAppSelector((state) => state.supplyChain);

  // Inbound shipments

  const hasMoreInboundShipmentsData = useAppSelector(
    (state) => state.supplyChain.supplyChain.hasMoreInboundShipmentsData
  );

  const [isLoadingInboundShipments, setIsLoadingInboundShipments] =
    useState(false);

  const [isLoadingInboundShipmentsError, setIsLoadingInboundShipmentsError] =
    useState(false);

  const [isInitialInboundShipmentsFetch, setIsInitialInboundShipmentsFetch] =
    useState(import.meta.env.MODE === 'development');

  const isLoadingInboundRef = useRef(false);
  const isInboundErrorRef = useRef(false);

  const fetchInboundShipments = useCallback(async (filters: ShipmentFilters) => {
    if (isLoadingInboundRef.current || isInboundErrorRef.current) {
      return;
    }

    isLoadingInboundRef.current = true;
    setIsLoadingInboundShipments(true);

    try {
      const data = await fetchShipments('Inbound', filters);

      data.hasMoreInboundShipmentsData = data.totalCount > 5;

      dispatch(addInboundShipments(data));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error: ', error);
      isInboundErrorRef.current = true;
      setIsLoadingInboundShipmentsError(true);
    } finally {
      isLoadingInboundRef.current = false;
      setIsLoadingInboundShipments(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outbound

  const [isLoadingOutboundShipments, setIsLoadingOutboundShipments] =
    useState(false);

  const [isLoadingOutboundShipmentsError, setIsLoadingOutboundShipmentsError] =
    useState(false);

  const [isInitialOutboundShipmentsFetch, setIsInitialOutboundShipmentsFetch] =
    useState(import.meta.env.MODE === 'development');

  const isLoadingOutboundRef = useRef(false);
  const isOutboundErrorRef = useRef(false);

  const fetchOutboundShipments = useCallback(async (filters: ShipmentFilters) => {
    if (isLoadingOutboundRef.current || isOutboundErrorRef.current) {
      return;
    }

    isLoadingOutboundRef.current = true;
    setIsLoadingOutboundShipments(true);

    try {
      const data = await fetchShipments('Outbound', filters);

      data.hasMoreOutboundShipmentsData = data.totalCount > 5;

      dispatch(addOutboundShipments(data));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error: ', error);
      isOutboundErrorRef.current = true;
      setIsLoadingOutboundShipmentsError(true);
    } finally {
      isLoadingOutboundRef.current = false;
      setIsLoadingOutboundShipments(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    inboundShipments,
    fetchInboundShipments,
    isInitialInboundShipmentsFetch,
    setIsInitialInboundShipmentsFetch,
    isLoadingInboundShipments,
    isLoadingInboundShipmentsError,
    hasMoreInboundShipmentsData,
    outboundShipments,
    fetchOutboundShipments,
  };
};
